import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft, Loader2, Save, KeyRound, ShieldCheck, Camera,
    Trash2, RotateCcw, Copy, User, Building2, Briefcase, Phone, Mail, FileText, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MenuAccessTab from "@/pages/personnel/MenuAccessTab";
import StatusBadge from "@/components/common/StatusBadge";
import Can, { useCan } from "@/components/common/Can";
import Forbidden from "@/pages/Forbidden";
import ConfirmDialog from "@/components/common/ConfirmDialog";

function initials(name) {
    if (!name) return "?";
    return name.split(" ").filter(Boolean).slice(0, 2).map(n => n[0]?.toUpperCase()).join("");
}

function Field({ label, Icon, children }) {
    return (
        <div className="space-y-1.5">
            <label className="block text-[12.5px] font-semibold text-foreground">{label}</label>
            <div className="relative">
                {Icon && <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />}
                {children}
            </div>
        </div>
    );
}

const inputCls = "h-11 w-full rounded-xl border border-border bg-background pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40";
const inputPlain = "h-11 w-full rounded-xl border border-border bg-background px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40";

export default function PersonnelDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const canView = useCan("employees.view");
    const canEdit = useCan("employees.edit");
    const canDelete = useCan("employees.delete");
    const canManage = useCan("employees.manage");

    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [form, setForm] = useState({});
    const [tempPassword, setTempPassword] = useState("");
    const [confirmReset, setConfirmReset] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [activityRows, setActivityRows] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [emp, deps, rls] = await Promise.all([
                api.get(`/employees/${id}`),
                api.get("/departments"),
                api.get("/roles"),
            ]);
            setEmployee(emp.data);
            setForm({
                first_name: emp.data.first_name || "",
                last_name: emp.data.last_name || "",
                phone: emp.data.phone || "",
                title: emp.data.title || "",
                department: emp.data.department || "",
                role: emp.data.role || "employee",
                status: emp.data.status || "active",
                birth_date: emp.data.birth_date ? emp.data.birth_date.slice(0, 10) : "",
                hire_date: emp.data.hire_date ? emp.data.hire_date.slice(0, 10) : "",
                notes: emp.data.notes || "",
                avatar_url: emp.data.avatar_url || "",
            });
            setDepartments(deps.data.items || []);
            setRoles(rls.data.items || []);

            // Try to load activity (audit) for this user
            try {
                const a = await api.get("/audit-logs", { params: { user_id: id, page_size: 20 } });
                setActivityRows(a.data.items || []);
            } catch (_) { /* no audit permission - ignore */ }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Personel bulunamadı.");
            navigate("/personnel");
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => { if (canView) load(); }, [load, canView]);

    if (!canView) return <Forbidden />;

    const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`/employees/${id}`, form);
            setEmployee(data);
            toast.success("Personel bilgileri güncellendi.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Güncellenemedi.");
        } finally {
            setSaving(false);
        }
    };

    const doReset = async () => {
        try {
            const { data } = await api.post(`/employees/${id}/reset-password`);
            setTempPassword(data.temp_password);
            toast.success(tr.personnel.detail.tempPasswordGenerated);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sıfırlanamadı.");
        } finally {
            setConfirmReset(false);
        }
    };

    const doDelete = async () => {
        try {
            await api.delete(`/employees/${id}`);
            toast.success("Personel silindi.");
            navigate("/personnel");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Silinemedi.");
        } finally {
            setConfirmDelete(false);
        }
    };

    if (loading || !employee) {
        return (
            <div className="flex items-center justify-center py-24 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> {tr.common.loading}
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <Link to="/personnel" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground mb-3">
                    <ArrowLeft className="h-3.5 w-3.5" /> {tr.personnel.title}
                </Link>
                <div className="card-elevated p-6">
                    <div className="flex flex-col sm:flex-row items-start gap-6">
                        <div className="relative shrink-0">
                            <Avatar className="h-24 w-24 border-4 border-background shadow-card">
                                <AvatarImage src={employee.avatar_url || undefined} alt={employee.full_name} />
                                <AvatarFallback className="bg-[hsl(var(--brand))] text-white text-2xl font-bold">
                                    {initials(employee.full_name)}
                                </AvatarFallback>
                            </Avatar>
                            <button className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(var(--brand))] text-white shadow-brand hover:bg-[hsl(var(--brand-hover))]" aria-label="Fotoğraf">
                                <Camera className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <h2 className="text-[22px] font-bold tracking-tight">{employee.full_name}</h2>
                                <StatusBadge status={employee.status || (employee.is_active ? "active" : "inactive")} />
                            </div>
                            <div className="mt-1 text-[13.5px] text-muted-foreground">{employee.title || "—"} · {employee.department || "—"}</div>
                            <div className="mt-1 text-[13px] text-muted-foreground">{employee.email}</div>
                            <div className="mt-4 flex flex-wrap gap-4 text-[12px] text-muted-foreground">
                                {employee.hire_date && (
                                    <div><span className="font-semibold text-foreground">İşe başlama: </span>{new Date(employee.hire_date).toLocaleDateString("tr-TR")}</div>
                                )}
                                {employee.last_login_at && (
                                    <div><span className="font-semibold text-foreground">Son giriş: </span>{new Date(employee.last_login_at).toLocaleString("tr-TR")}</div>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                            {canManage && (
                                <button onClick={() => setConfirmReset(true)} className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-[12.5px] font-semibold hover:bg-muted" data-testid="btn-reset-password">
                                    <RotateCcw className="h-4 w-4" /> {tr.personnel.detail.resetPassword}
                                </button>
                            )}
                            {canDelete && employee.role !== "super_admin" && (
                                <button onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-2 rounded-xl border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5 text-[hsl(var(--destructive))] px-3 py-2 text-[12.5px] font-semibold hover:bg-[hsl(var(--destructive))]/10" data-testid="btn-delete">
                                    <Trash2 className="h-4 w-4" /> {tr.common.delete}
                                </button>
                            )}
                        </div>
                    </div>

                    {tempPassword && (
                        <div className="mt-5 rounded-2xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5 p-4">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div>
                                    <div className="text-[13px] font-semibold">{tr.personnel.detail.tempPasswordGenerated}</div>
                                    <div className="text-[12px] text-muted-foreground">Lütfen bu şifreyi personelle güvenli şekilde paylaşın. Sadece bir kez görüntülenir.</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <code className="rounded-lg bg-background border border-border px-3 py-2 text-[13px] font-mono">{tempPassword}</code>
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(tempPassword); toast.success("Kopyalandı."); }}
                                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-muted"
                                        aria-label="Kopyala"
                                    >
                                        <Copy className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="personal" className="space-y-4">
                <TabsList className="bg-muted/60 p-1 rounded-xl">
                    <TabsTrigger value="personal" className="data-[state=active]:bg-background data-[state=active]:shadow-card rounded-lg">{tr.personnel.detail.personalInfo}</TabsTrigger>
                    <TabsTrigger value="contact" className="data-[state=active]:bg-background data-[state=active]:shadow-card rounded-lg">{tr.personnel.detail.contactInfo}</TabsTrigger>
                    <TabsTrigger value="work" className="data-[state=active]:bg-background data-[state=active]:shadow-card rounded-lg">İş Bilgileri</TabsTrigger>
                    <TabsTrigger value="security" className="data-[state=active]:bg-background data-[state=active]:shadow-card rounded-lg">{tr.profile.security}</TabsTrigger>
                    <TabsTrigger value="history" className="data-[state=active]:bg-background data-[state=active]:shadow-card rounded-lg">{tr.personnel.detail.loginHistory}</TabsTrigger>
                    <TabsTrigger value="menu-access" className="data-[state=active]:bg-background data-[state=active]:shadow-card rounded-lg" data-testid="tab-menu-access">Menü Yetkileri</TabsTrigger>
                </TabsList>

                <TabsContent value="personal">
                    <div className="card-elevated p-6 space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label={tr.common.firstName} Icon={User}>
                                <input value={form.first_name} onChange={on("first_name")} className={inputCls} disabled={!canEdit} data-testid="detail-first-name" />
                            </Field>
                            <Field label={tr.common.lastName} Icon={User}>
                                <input value={form.last_name} onChange={on("last_name")} className={inputCls} disabled={!canEdit} data-testid="detail-last-name" />
                            </Field>
                            <Field label={tr.common.birthDate} Icon={Calendar}>
                                <input type="date" value={form.birth_date} onChange={on("birth_date")} className={inputCls} disabled={!canEdit} />
                            </Field>
                            <Field label="Profil Fotoğrafı URL" Icon={User}>
                                <input value={form.avatar_url} onChange={on("avatar_url")} className={inputCls} disabled={!canEdit} placeholder="https://…" />
                            </Field>
                        </div>
                        {canEdit && (
                            <div className="flex justify-end">
                                <button onClick={save} disabled={saving} className="btn-brand h-11" data-testid="detail-save-personal">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {tr.common.save}
                                </button>
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="contact">
                    <div className="card-elevated p-6 space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label={tr.common.email} Icon={Mail}>
                                <input value={employee.email} disabled className={`${inputCls} bg-muted/60 cursor-not-allowed`} />
                            </Field>
                            <Field label={tr.common.phone} Icon={Phone}>
                                <input value={form.phone} onChange={on("phone")} className={inputCls} disabled={!canEdit} data-testid="detail-phone" />
                            </Field>
                        </div>
                        {canEdit && (
                            <div className="flex justify-end">
                                <button onClick={save} disabled={saving} className="btn-brand h-11">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {tr.common.save}
                                </button>
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="work">
                    <div className="card-elevated p-6 space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label={tr.common.department} Icon={Building2}>
                                <select value={form.department} onChange={on("department")} className={inputPlain} disabled={!canEdit} data-testid="detail-department">
                                    <option value="">{tr.common.none}</option>
                                    {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                                </select>
                            </Field>
                            <Field label={tr.common.title} Icon={Briefcase}>
                                <input value={form.title} onChange={on("title")} className={inputCls} disabled={!canEdit} data-testid="detail-title" />
                            </Field>
                            <Field label={tr.common.role} Icon={ShieldCheck}>
                                <select value={form.role} onChange={on("role")} className={inputPlain} disabled={!canManage} data-testid="detail-role">
                                    {roles.map((r) => <option key={r.name} value={r.name}>{r.display_name}</option>)}
                                </select>
                            </Field>
                            <Field label={tr.common.status} Icon={ShieldCheck}>
                                <select value={form.status} onChange={on("status")} className={inputPlain} disabled={!canEdit} data-testid="detail-status">
                                    <option value="active">{tr.status.active}</option>
                                    <option value="inactive">{tr.status.inactive}</option>
                                    <option value="on_leave">{tr.status.on_leave}</option>
                                    <option value="terminated">{tr.status.terminated}</option>
                                </select>
                            </Field>
                            <Field label={tr.common.hireDate} Icon={Calendar}>
                                <input type="date" value={form.hire_date} onChange={on("hire_date")} className={inputCls} disabled={!canEdit} />
                            </Field>
                            <div className="md:col-span-2">
                                <Field label={tr.common.notes} Icon={FileText}>
                                    <textarea value={form.notes} onChange={on("notes")} rows={4} className="w-full rounded-xl border border-border bg-background pl-10 pr-3 pt-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" disabled={!canEdit} />
                                </Field>
                            </div>
                        </div>
                        {canEdit && (
                            <div className="flex justify-end">
                                <button onClick={save} disabled={saving} className="btn-brand h-11" data-testid="detail-save-work">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {tr.common.save}
                                </button>
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="security">
                    <div className="card-elevated p-6 space-y-5">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]"><KeyRound className="h-5 w-5" /></div>
                            <div>
                                <div className="text-[15px] font-semibold">{tr.personnel.detail.passwordChange}</div>
                                <div className="text-[12.5px] text-muted-foreground">Personel için geçici bir şifre oluşturun; sisteme giriş yaptığında değiştirmelidir.</div>
                            </div>
                        </div>
                        <div>
                            {canManage ? (
                                <button onClick={() => setConfirmReset(true)} className="btn-brand h-11" data-testid="btn-generate-temp">
                                    <RotateCcw className="h-4 w-4" /> Geçici Şifre Oluştur
                                </button>
                            ) : (
                                <div className="text-[13px] text-muted-foreground">Bu işlem için yetkiniz yok.</div>
                            )}
                        </div>

                        <div className="pt-4 border-t border-border">
                            <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-muted-foreground"><ShieldCheck className="h-5 w-5" /></div>
                                <div>
                                    <div className="text-[15px] font-semibold">{tr.personnel.detail.twoFactor}</div>
                                    <div className="text-[12.5px] text-muted-foreground">{tr.personnel.detail.twoFactorComingSoon}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="history">
                    <div className="card-elevated p-6">
                        <div className="mb-4">
                            <div className="text-[15px] font-semibold">{tr.personnel.detail.loginHistory}</div>
                            <div className="text-[12.5px] text-muted-foreground mt-0.5">Bu kullanıcının son aktiviteleri</div>
                        </div>
                        {activityRows.length === 0 ? (
                            <div className="py-8 text-center text-muted-foreground text-[13px]">Kayıt bulunamadı.</div>
                        ) : (
                            <ul className="space-y-2">
                                {activityRows.map((a) => (
                                    <li key={a.id} className="flex items-center justify-between rounded-xl border border-border bg-background px-3 py-2">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-[10px] font-bold uppercase">{a.action?.slice(0, 2) || "?"}</div>
                                            <div className="min-w-0">
                                                <div className="text-[13px] font-semibold truncate">{a.action} <span className="text-muted-foreground font-normal">· {a.module || "—"}</span></div>
                                                <div className="text-[11.5px] text-muted-foreground truncate">{a.entity || "—"} · {a.ip || "—"}</div>
                                            </div>
                                        </div>
                                        <div className="text-[11.5px] text-muted-foreground whitespace-nowrap">
                                            {new Date(a.timestamp).toLocaleString("tr-TR")}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="menu-access">
                    <MenuAccessTab userId={id} />
                </TabsContent>
            </Tabs>

            <ConfirmDialog
                open={confirmReset}
                onOpenChange={setConfirmReset}
                title="Şifreyi sıfırla"
                description="Geçici bir şifre oluşturulacak. Personelin mevcut şifresi geçersiz olacaktır. Devam edilsin mi?"
                confirmText="Evet, sıfırla"
                variant="danger"
                onConfirm={doReset}
            />
            <ConfirmDialog
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                title={tr.personnel.detail.confirmDelete}
                description={tr.personnel.detail.confirmDeleteBody}
                confirmText={tr.common.yesDelete}
                variant="danger"
                onConfirm={doDelete}
            />
        </div>
    );
}
